import { getChangedItemQuantity } from "components/pages/BelongingsPage/tools/surveyAtom";
import { Attr, BelongsTo, HasMany, Model } from "spraypaint";
import { ShipmentWeightBreakdowns } from "types";
import { ApplicationRecord } from "./ApplicationRecord";
import { Order } from "./Order";
import { SurveyItem } from "./SurveyItem";
import { SurveySegment } from "./SurveySegment";
import { SurveyService } from "./SurveyService";

const hasChangedItems = (items: Record<string, number>) => {
  const changedItemsVals = Object.values(items).filter((item) => item !== 0);
  return changedItemsVals.length !== 0;
};
@Model()
export class Survey extends ApplicationRecord {
  static jsonapiType = "surveys";

  @Attr() isComplete!: boolean;
  @Attr() surveyType!: "VIRTUAL" | "ONSITE" | "SELF_GUIDED";
  @Attr() isPrimary!: boolean;
  @Attr() isPendingApproval!: boolean;
  @Attr() estimatedWeight!: number;
  @Attr() estimatedProGearWeight!: number;
  @Attr() estimatedSpouseProGearWeight!: number;
  @Attr() notes?: string;
  @Attr() serviceId!: number;
  @HasMany() segments!: SurveySegment[];
  @HasMany() items!: SurveyItem[];

  get itemsNonProGear() {
    return this.items.filter(
      ({ category }) => category?.toUpperCase() === "HOUSEHOLD_GOODS"
    );
  }

  get itemsGunSafe() {
    return this.items.filter(({ definitionId }) => definitionId === "2248");
  }

  getEstimatedWeightBreakDown(changedItems: Record<string, number>) {
    if (this.surveyType === "SELF_GUIDED" && this.items.length <= 0) {
      // Self Guided surveys from Phase 1 do not have items, so we use the survey estimated weight
      const { number: orderNumber, id: orderId, shipmentType } = this.order;
      return {
        [orderNumber]: {
          orderId: orderId!,
          shipmentType,
          weight: this.estimatedWeight,
        },
      };
    }

    return this.itemsNonProGear.reduce((breakdown, item) => {
      const changedQuantity = hasChangedItems(changedItems)
        ? getChangedItemQuantity(
            changedItems,
            item.segment.order.number,
            item.roomId ?? "",
            item.id
          )
        : 0;

      const orderNumber = item.segment.order.number;
      breakdown[orderNumber] = {
        shipmentType: item.segment.order.shipmentType,
        weight:
          (breakdown[orderNumber]?.weight ?? 0) +
          item.getTotalWeight(changedQuantity),
        orderId: item.segment.order.id!,
      };
      return breakdown;
    }, {} as ShipmentWeightBreakdowns);
  }

  @BelongsTo(Order) order!: Order;
  @BelongsTo(SurveyService) service!: SurveyService;
}
